import { Component, input, computed } from '@angular/core';
import { NotificationLayoutComponent } from '../../notification-detail-layout/notification-detail-layout.component';
import { NotificationStep, UsageBucket } from '../../../core/models/notification.model';
import { PercentPipe } from '@angular/common';

@Component({
  selector: 'wf-time-usage-notification',
  imports: [
    NotificationLayoutComponent,
    PercentPipe
  ],
  template: `
    <wf-notification-layout
      headerTitle="📊 Your weekly WordFlight usage update"
      [preambles]="[{
        text: 'Here’s how your students did last week. The sweet spot for best results is <b>60–80 minutes per week</b> — '
          + 'here’s how many students hit the mark:'
        },
        ]"
      [steps]="steps"
      ctaLabel="Open Group Summary report"
      ctaRoute="/reports/system/summary/group"
      [usageHighlight]="highlightStudents()"
    >
      <table table class="table-wrap">
        <tr>
          <th class="th">Minutes Last Week</th>
          <th class="th">Students</th>
          <th class="th">%</th>
        </tr>
        @for (bucket of buckets(); track bucket.key) {
          <tr>
            <td class="td">{{ bucket.label ?? '-' }} {{ bucket.target ? ' ✅' : '' }}</td>
            <td class="td" style="white-space:nowrap; color:#a2a2a2;">{{ bucket.count ?? 0 }}</td>
            <td class="td">{{ (totalStudents() ? bucket.count / totalStudents() : 0) | percent:'1.0-0' }}</td>
          </tr>
        }
      </table>
    </wf-notification-layout>
  `,
  standalone: true
})
export class TimeUsageNotificationComponent {
  readonly data = input.required<any>();
  readonly steps: NotificationStep[] = [
    {
      title: 'Celebrate students who hit their goal',
      body: 'Open the Group Summary report and check the <b>Previous Week Usage</b> column. Give a shoutout to students who met 60–80 minutes — recognition is a powerful motivator.',
      variant: 'green',
    },
    {
      title: 'Check in with students who fell short',
      body: 'Make sure they have protected time this week to reach their goal. Even small increases in usage add up to real growth.'
    },
    {
      title: 'Need engagement ideas?',
      body: 'Visit the <b>Student Engagement</b> section in the Management Site for goal-setting tools and motivation resources. Or reach out to your Partner Success Lead for personalized suggestions.',
      variant: 'warn',
    },
  ];

  readonly buckets = computed(() => {
    const data = this.data();
    const rawBuckets = data?.[0]?.buckets ?? [];

    return [...rawBuckets].sort((a, b) => {
      if (a.max === b.max) return 0;
      if (a.max === null) return -1;
      if (b.max === null) return 1;
      return b.max - a.max;
    });
  });

  readonly totalStudents = computed(() => {
    const buckets = this.buckets();
    if (!buckets) return 1;

    return buckets.reduce((acc: number, bucket: UsageBucket) => acc += bucket.count, 0);
  });

  readonly highlightStudents = computed(() => {
    const buckets = this.buckets();
    if (!buckets) return '0';

    const meetingGoal = buckets.reduce((acc: number, bucket: UsageBucket) => acc += (bucket?.min ?? 0 < 60) ? bucket.count : 0, 0);
    return meetingGoal ? `${meetingGoal}` : '0';
  });
}
